import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../../services/api';
import { AuthPageShell, fieldClass, primaryButtonClass } from './AuthPageShell';

/**
 * M7 (security review, 2026-08-17): the backend has always issued
 * `/tilbakestill-passord/:token` links (see
 * backend/src/auth/emailDelivery.ts buildPasswordResetLink), but this
 * frontend route did not exist — every password-reset link was dead. This
 * mirrors InvitationPage's shape (token from the URL, one form, redirect
 * to /login on success) but does not pre-validate the token against a GET
 * endpoint first (there isn't one for reset tokens, unlike invitations) —
 * it simply submits and surfaces the backend's error.
 */
export function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!token) return;
    setPending(true);
    setError(null);
    try {
      await api.confirmPasswordReset(token, password);
      setDone(true);
    } catch {
      setError('Kunne ikke tilbakestille passordet. Lenken kan være ugyldig eller utløpt (gyldig i 1 time), eller passordet må være minst 12 tegn.');
    } finally {
      setPending(false);
    }
  }

  if (done) {
    return (
      <AuthPageShell title="Passord tilbakestilt" lead="">
        <p className="text-sm text-black/70">Passordet er endret. Alle tidligere økter er logget ut.</p>
        <button className={`${primaryButtonClass} mt-6`} onClick={() => navigate('/login', { replace: true })}>
          Gå til innlogging
        </button>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell title="Velg nytt passord" lead="">
      <form onSubmit={submit} className="space-y-5">
        <label className="block text-sm font-medium">
          Nytt passord (minst 12 tegn)
          <input
            className={fieldClass}
            type="password"
            autoComplete="new-password"
            minLength={12}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        {error && (
          <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
            {error}
          </p>
        )}
        <button className={primaryButtonClass} disabled={pending || !token} type="submit">
          {pending ? 'Lagrer…' : 'Sett nytt passord'}
        </button>
      </form>
    </AuthPageShell>
  );
}
